/**
 * Cart drawer recommendations — Mar Amoli
 *
 * Popula <aside data-ma-rec-pane> con productos recomendados al abrir el
 * cart drawer. Estrategia:
 *   1) Lee /cart.js para obtener los product_id y tipos en carrito.
 *   2) Ofrece COMPLEMENTOS: accesorios que acompañan a lo que ya lleva
 *      (jabonera para el jabón, bolsa de sisal, cajita si parece regalo).
 *      Ver el bloque "Complementos, no más de lo mismo" más abajo.
 *   2b) Si no alcanzan, cae a Product Recommendations API con
 *      intent=complementary y luego related.
 *   3) Mergea, scorea (boost si aparece en varios resultados), excluye
 *      productos ya en carrito, toma top 4.
 *   4) Renderea cards con createElement+textContent (XSS-safe).
 *   5) Si no hay items en carrito, oculta la pane.
 *   6) Si la API falla o devuelve 0, intenta el fallback collection del
 *      schema (addons_collection) via /collections/{handle}/products.json.
 *
 * Hook: escucha el evento 'ma:cart:open' (despachado por window.MaCart.open
 * y por la apertura interna del drawer). Cachea el resultado para la sesión
 * (sessionStorage) para evitar fetches repetidos en la misma navegación,
 * pero invalida cuando cambia el carrito (eventos de add/change).
 *
 * Seguridad: todos los datos del producto provienen de la API de Shopify
 * (mismo origen). Aún así, render usa createElement+textContent para evitar
 * XSS si Shopify devolviera HTML en algún campo.
 */
(function () {
  "use strict";

  var DRAWER_SEL = "ma-cart-drawer";
  var PANE_SEL = "[data-ma-rec-pane]";
  var LIST_SEL = "[data-ma-rec-list]";
  var EYEBROW_SEL = "[data-ma-rec-eyebrow]";
  var LEAD_SEL = "[data-ma-rec-lead]";
  var MOBILE_SEL = "[data-ma-rec-mobile]";
  var MOBILE_LIST_SEL = "[data-ma-rec-mobile-list]";
  var MOBILE_EYEBROW_SEL = "[data-ma-rec-mobile-eyebrow]";
  var MOBILE_LEAD_SEL = "[data-ma-rec-mobile-lead]";

  var CACHE_KEY = "ma-cart-recos-v1";
  var CACHE_TTL_MS = 5 * 60 * 1000; // 5 min — invalidar si cambia carrito

  var state = {
    loaded: false,
    inFlight: false,
    lastCartSig: null,
  };

  function $(sel, root) {
    return (root || document).querySelector(sel);
  }
  function $$(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  function formatMoney(cents) {
    var n = (cents || 0) / 100;
    try {
      return n.toLocaleString("es-MX", {
        style: "currency",
        currency: window.Shopify && window.Shopify.currency ? window.Shopify.currency.active : "MXN",
        maximumFractionDigits: n % 1 === 0 ? 0 : 2,
      });
    } catch (e) {
      return "$" + n.toFixed(0) + " MXN";
    }
  }

  function getCartSignature(cart) {
    if (!cart || !cart.items) return "empty";
    return cart.items
      .map(function (it) {
        return it.product_id + ":" + it.quantity;
      })
      .sort()
      .join("|");
  }

  function readCache() {
    try {
      var raw = sessionStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      var data = JSON.parse(raw);
      if (!data || typeof data !== "object") return null;
      if (Date.now() - data.t > CACHE_TTL_MS) return null;
      return data;
    } catch (e) {
      return null;
    }
  }

  function writeCache(sig, products) {
    try {
      sessionStorage.setItem(
        CACHE_KEY,
        JSON.stringify({ t: Date.now(), sig: sig, products: products })
      );
    } catch (e) {
      // Quota exceeded or disabled — silent
    }
  }

  function fetchJSON(url) {
    return fetch(url, { credentials: "same-origin" }).then(function (r) {
      if (!r.ok) throw new Error("HTTP " + r.status);
      return r.json();
    });
  }

  function fetchRecsFor(productId, intent) {
    var url =
      "/recommendations/products.json?product_id=" +
      encodeURIComponent(productId) +
      "&intent=" +
      encodeURIComponent(intent) +
      "&limit=6";
    return fetchJSON(url)
      .then(function (data) {
        return (data && data.products) || [];
      })
      .catch(function () {
        return [];
      });
  }

  function fetchFallbackCollection(handle) {
    if (!handle) return Promise.resolve([]);
    var url = "/collections/" + encodeURIComponent(handle) + "/products.json?limit=12";
    return fetchJSON(url)
      .then(function (data) {
        return (data && data.products) || [];
      })
      .catch(function () {
        return [];
      });
  }

  /**
   * Toma productos de varios fetches y los scorea.
   * Score base = 1 por aparición. Boost si la primera variante está available.
   */
  function scoreAndMerge(productLists, excludeIds) {
    var bag = {};
    productLists.forEach(function (list) {
      (list || []).forEach(function (p, idx) {
        if (!p || !p.id) return;
        if (excludeIds.indexOf(p.id) !== -1) return;
        var key = String(p.id);
        if (!bag[key]) {
          bag[key] = { product: p, score: 0 };
        }
        // Posición temprana en lista => mejor reco
        bag[key].score += Math.max(1, 6 - idx);
      });
    });
    var arr = Object.keys(bag).map(function (k) {
      return bag[k];
    });
    arr.sort(function (a, b) {
      return b.score - a.score;
    });
    return arr.map(function (entry) {
      return entry.product;
    });
  }


  /* ── Complementos, no más de lo mismo ─────────────────────────────────────
     Antes se pedía a Shopify intent=complementary, que en la práctica devuelve
     productos parecidos: con jabones en el carrito ofrecía más jabones. Eso no
     vende, porque quien ya eligió su jabón no quiere otro igual.

     La idea ahora es la de una caja de tienda de conveniencia: lo pequeño que
     completa lo que ya llevas. Si hay jabones, una jabonera o una bolsa de
     sisal; si hay barras de shampoo, algo donde escurrirlas; si el carrito
     parece un regalo, una cajita.

     Los accesorios salen de una colección que ya existía en la tienda
     (accesorios-regalos-carrito) y se ordenan por cuánto pegan con lo que hay
     dentro. A igualdad, primero lo más barato: es una compra por impulso. */

  var ACCESORIOS_HANDLES = ["accesorios-ecologicos", "accesorios-regalos-carrito"];
  var ROTACION_KEY = "ma-recos-rotacion";

  /* Qué acompaña a qué. Se busca en el título del accesorio, en minúsculas y
     sin acentos, para no depender de cómo esté escrito el tipo de producto. */
  var COMPLEMENTOS = [
    {
      cuando: ["jabon", "premium", "postre"],
      acompanan: ["jabonera", "sisal", "estuche de viaje para jabon", "estropajo"],
    },
    {
      cuando: ["shampoo", "acondicionador"],
      acompanan: ["estuche de viaje para shampoo", "cepillo de bambu para cabello", "jabonera"],
    },
    {
      cuando: ["crema", "balsamo", "stick", "desodorante"],
      acompanan: ["konjac", "estropajo", "cepillo corporal", "esponja"],
    },
  ];

  /* Familias para el hallazgo: algo que el carrito no tiene todavía. Rotan en
     cada apertura para que quien vuelve no vea siempre lo mismo, y así se ve
     con el tiempo qué categoría responde mejor desde aquí. */
  var DESCUBRIMIENTOS = [
    { familia: "higiene bucal", pistas: ["pasta dental", "hilo dental", "limpiador de lengua", "cepillo de dientes", "cotonetes"] },
    { familia: "cuidado corporal", pistas: ["konjac", "cepillo corporal", "estropajo"] },
    { familia: "cocina", pistas: ["lavatrastes", "esponja multiusos"] },
    { familia: "viaje", pistas: ["estuche de viaje", "bolsa de algodon"] },
  ];

  function sinAcentos(t) {
    return String(t || "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
  }

  /** Cuántas unidades hay en el carrito de cada familia de producto. */
  function perfilDelCarrito(cart) {
    var conteo = {};
    (cart.items || []).forEach(function (it) {
      var texto = sinAcentos(it.product_type + " " + it.product_title);
      var qty = it.quantity || 1;
      COMPLEMENTOS.forEach(function (regla) {
        regla.cuando.forEach(function (palabra) {
          if (texto.indexOf(palabra) !== -1) {
            regla.acompanan.forEach(function (acc) {
              conteo[acc] = (conteo[acc] || 0) + qty;
            });
          }
        });
      });
    });
    return conteo;
  }

  /** Turno actual de la rotación; avanza en cada apertura del carrito. */
  function turno() {
    var n = 0;
    try {
      n = parseInt(sessionStorage.getItem(ROTACION_KEY) || "0", 10) || 0;
      sessionStorage.setItem(ROTACION_KEY, String(n + 1));
    } catch (e) {
      /* sessionStorage bloqueado: siempre el primer turno */
    }
    return n;
  }

  /**
   * Arma la lista final: primero lo que acompaña a lo que ya lleva, y siempre
   * un par de hallazgos de familias que el carrito no toca.
   *
   * Las cajas de regalo pesan sólo cuando hay varias cosas distintas: ahí es
   * cuando suele ser para regalar. A igualdad, primero lo más barato, que es
   * compra por impulso.
   */
  function ordenarComplementos(accesorios, cart, excluirIds) {
    var conteo = perfilDelCarrito(cart);
    var variedad = (cart.items || []).length;
    var enCarrito = sinAcentos(
      (cart.items || [])
        .map(function (it) {
          return it.product_type + " " + it.product_title;
        })
        .join(" ")
    );
    var vuelta = turno();

    var puntuados = accesorios
      .filter(function (p) {
        return p && p.id && excluirIds.indexOf(p.id) === -1;
      })
      .map(function (p) {
        var titulo = sinAcentos(p.title);
        var puntos = 0;
        Object.keys(conteo).forEach(function (clave) {
          if (titulo.indexOf(clave) !== -1) puntos += conteo[clave];
        });
        if (titulo.indexOf("caja") !== -1 || titulo.indexOf("cajita") !== -1) {
          puntos += variedad >= 3 ? 4 : 1;
        }
        var centavos = precioEnCentavos(p);
        return { producto: p, puntos: puntos, precio: centavos || 0, titulo: titulo };
      });

    var afines = puntuados
      .filter(function (e) {
        return e.puntos > 0;
      })
      .sort(function (a, b) {
        if (b.puntos !== a.puntos) return b.puntos - a.puntos;
        return a.precio - b.precio;
      })
      .map(function (e) {
        return e.producto;
      });

    // Hallazgos: familias que el carrito no toca, rotando cuál toca hoy.
    var hallazgos = [];
    var ausentes = DESCUBRIMIENTOS.filter(function (d) {
      return !d.pistas.some(function (pista) {
        return enCarrito.indexOf(pista) !== -1;
      });
    });
    for (var i = 0; i < ausentes.length && hallazgos.length < 2; i++) {
      var d = ausentes[(vuelta + i) % ausentes.length];
      var candidatos = puntuados.filter(function (e) {
        return (
          d.pistas.some(function (pista) {
            return e.titulo.indexOf(pista) !== -1;
          }) &&
          afines.indexOf(e.producto) === -1 &&
          hallazgos.indexOf(e.producto) === -1
        );
      });
      if (candidatos.length) {
        // Dentro de la familia también rota, para no repetir el mismo siempre.
        hallazgos.push(candidatos[(vuelta + i) % candidatos.length].producto);
      }
    }

    return afines.slice(0, 4).concat(hallazgos);
  }

  function getFirstAvailableVariant(p) {
    if (!p || !p.variants) return null;
    for (var i = 0; i < p.variants.length; i++) {
      if (p.variants[i].available) return p.variants[i];
    }
    return p.variants[0] || null;
  }

  /**
   * Precio en centavos, venga de donde venga.
   *
   * /recommendations/products.json trae `price` en el producto y en centavos.
   * /collections/x/products.json NO trae precio de producto: está en la
   * variante y como texto en pesos ("55.00"). Al ampliar el fondo con
   * colecciones, todos los accesorios salían en $0.
   */
  function precioEnCentavos(p, variant) {
    if (p && typeof p.price === "number") return p.price;
    if (p && p.price != null && p.price !== "") {
      var n = parseFloat(p.price);
      if (isFinite(n)) return Math.round(n * 100);
    }
    var v = variant || getFirstAvailableVariant(p);
    if (v && typeof v.price === "number") return v.price;
    if (v && v.price != null) {
      var m = parseFloat(v.price);
      if (isFinite(m)) return Math.round(m * 100);
    }
    return null;
  }

  function getImageUrl(p) {
    if (!p) return null;
    if (p.featured_image) {
      // /products.json devuelve string; recommendations API devuelve objeto
      if (typeof p.featured_image === "string") return p.featured_image;
      if (p.featured_image.src) return p.featured_image.src;
    }
    if (p.images && p.images[0]) {
      if (typeof p.images[0] === "string") return p.images[0];
      if (p.images[0].src) return p.images[0].src;
    }
    return null;
  }

  function buildSizedImageUrl(src, width) {
    if (!src) return null;
    // Inserta width en el path de Shopify CDN si es posible
    try {
      var u = new URL(src, window.location.origin);
      u.searchParams.set("width", String(width));
      return u.toString();
    } catch (e) {
      return src;
    }
  }


  /* ── Calificaciones ───────────────────────────────────────────────────────
     Los endpoints de producto (/recommendations/products.json,
     /collections/x/products.json) NO devuelven metafields, y el rating vive en
     reviews.rating / reviews.rating_count. Llega de
     sections/ma-ratings-json.liquid vía Section Rendering API: un fetch por
     sesión (~5 KB), cacheado en sessionStorage.
     Ver docs/LEARNINGS.md. */
  var RATINGS_KEY = "ma-ratings-v1";
  var RATINGS = null;
  var ratingsPromise = null;

  function getRatings() {
    if (ratingsPromise) return ratingsPromise;
    try {
      var cache = sessionStorage.getItem(RATINGS_KEY);
      if (cache) {
        RATINGS = JSON.parse(cache);
        ratingsPromise = Promise.resolve(RATINGS);
        return ratingsPromise;
      }
    } catch (e) {
      /* sessionStorage bloqueado: seguimos por red */
    }
    ratingsPromise = fetch("/?section_id=ma-ratings-json", { credentials: "same-origin" })
      .then(function (r) {
        return r.text();
      })
      .then(function (html) {
        // DOMParser en vez de innerHTML: no ejecuta nada de lo que llega.
        var doc = new DOMParser().parseFromString(html, "text/html");
        var el = doc.getElementById("ma-ratings-data");
        var data = el ? JSON.parse(el.textContent) : {};
        try {
          sessionStorage.setItem(RATINGS_KEY, JSON.stringify(data));
        } catch (e) {
          /* cuota llena: da igual, se vuelve a pedir */
        }
        return data;
      })
      .catch(function () {
        return {};
      });
    return ratingsPromise;
  }

  /** Pastilla de estrellas, mismas clases que ma-product-card.liquid. */
  /**
   * Cinco estrellas dibujadas más el número de reseñas, igual que en la lista
   * del carrito. Antes era una estrella y el promedio en cifra ("★ 5 (8)"):
   * se lee como un dato, no como una valoración. Las cinco estrellas se
   * reconocen de un vistazo y es lo que la gente espera ver.
   *
   * Reutiliza las clases del carrito para que ambos sitios cambien juntos.
   */
  function buildRatingBadge(handle) {
    var dato = RATINGS && RATINGS[handle];
    if (!dato || !dato[1]) return null;
    var valor = dato[0];
    var count = dato[1];

    var badge = document.createElement("span");
    badge.className = "ma-cart__item-rating ma-cart__item-rating--estatica";
    badge.setAttribute("aria-label", valor + " de 5 estrellas · " + count + " reseñas");

    var estrellas = document.createElement("span");
    estrellas.className = "ma-cart__item-stars";
    estrellas.setAttribute("aria-hidden", "true");
    for (var i = 1; i <= 5; i++) {
      var e = document.createElement("span");
      // Media estrella a partir de .5, como en el carrito.
      e.className = valor >= i ? "star star--full" : valor >= i - 0.5 ? "star star--half" : "star";
      e.textContent = "★";
      estrellas.appendChild(e);
    }
    badge.appendChild(estrellas);

    var c = document.createElement("span");
    c.className = "ma-cart__item-rating-count";
    c.textContent = "(" + count + ")";
    badge.appendChild(c);

    return badge;
  }

  /** Marca el contenedor y le pone el badge si ya tenemos el dato. */
  function attachRating(contenedor, handle) {
    if (!contenedor || !handle) return;
    contenedor.setAttribute("data-ma-rating-for", handle);
    if (contenedor.querySelector(".ma-card__rating-badge")) return;
    var badge = buildRatingBadge(handle);
    if (badge) contenedor.appendChild(badge);
  }

  /** Rellena las tarjetas que se pintaron antes de que llegara el mapa. */
  function fillPendingRatings() {
    $$("[data-ma-rating-for]").forEach(function (el) {
      if (el.querySelector(".ma-card__rating-badge")) return;
      var badge = buildRatingBadge(el.getAttribute("data-ma-rating-for"));
      if (badge) el.appendChild(badge);
    });
  }

  getRatings().then(function (mapa) {
    RATINGS = mapa || {};
    fillPendingRatings();
  });

  /**
   * Las calificaciones se cargan una sola vez por sesión, así que el mapa vive
   * aquí y lo comparte quien pinte tarjetas en JS (hoy: vistos recientemente).
   * Marcar el hueco con data-ma-rating-for basta — attach lo llena si el mapa
   * ya llegó, y fillPendingRatings() lo alcanza si aún no.
   */
  window.MaRatings = {
    attach: attachRating,
    fill: fillPendingRatings,
  };

  function renderCard(p) {
    var card = document.createElement("div");
    card.className = "ma-drawer__rec-card";

    var variant = getFirstAvailableVariant(p);
    var imgUrl = getImageUrl(p);
    var url = p.url || (p.handle ? "/products/" + p.handle : "#");

    // Image link
    var imgLink = document.createElement("a");
    imgLink.className = "ma-drawer__rec-img";
    imgLink.href = url;
    imgLink.setAttribute("aria-label", p.title || "Producto");
    if (imgUrl) {
      var img = document.createElement("img");
      img.src = buildSizedImageUrl(imgUrl, 200);
      img.alt = p.title || "";
      img.width = 80;
      img.height = 100;
      img.loading = "lazy";
      imgLink.appendChild(img);
    }
    card.appendChild(imgLink);

    // Body
    var body = document.createElement("div");
    body.className = "ma-drawer__rec-body";
    var nameLink = document.createElement("a");
    nameLink.className = "ma-drawer__rec-name";
    nameLink.href = url;
    nameLink.textContent = p.title || "";
    body.appendChild(nameLink);
    // Las estrellas iban sobre la miniatura de 80px, donde no se leían.
    // Aquí van donde se buscan: entre el nombre y el precio.
    var estrellas = document.createElement("div");
    estrellas.className = "ma-drawer__rec-rating";
    body.appendChild(estrellas);
    attachRating(estrellas, p.handle);
    var price = document.createElement("div");
    price.className = "ma-drawer__rec-price";
    var priceCents = precioEnCentavos(p, variant);
    price.textContent = priceCents == null ? "" : formatMoney(priceCents);
    body.appendChild(price);
    card.appendChild(body);

    // ATC button or sold-out marker
    if (variant && variant.available) {
      var btn = document.createElement("button");
      btn.type = "button";
      btn.className = "ma-drawer__rec-add";
      btn.setAttribute("data-ma-card-add", "");
      btn.setAttribute("data-variant-id", String(variant.id));
      btn.setAttribute("aria-label", "Agregar " + (p.title || "producto") + " al carrito");
      btn.textContent = "+";
      card.appendChild(btn);
    } else {
      var sold = document.createElement("span");
      sold.className = "ma-drawer__rec-sold";
      sold.setAttribute("aria-label", "Agotado");
      sold.textContent = "—";
      card.appendChild(sold);
    }

    return card;
  }

  function renderMobileCard(p) {
    var card = document.createElement("div");
    card.className = "ma-cart__rec-mobile-card";
    card.setAttribute("role", "listitem");

    var variant = getFirstAvailableVariant(p);
    var imgUrl = getImageUrl(p);
    var url = p.url || (p.handle ? "/products/" + p.handle : "#");

    var imgLink = document.createElement("a");
    imgLink.className = "ma-cart__rec-mobile-img";
    imgLink.href = url;
    imgLink.setAttribute("aria-label", p.title || "Producto");
    if (imgUrl) {
      var img = document.createElement("img");
      img.src = buildSizedImageUrl(imgUrl, 280);
      img.alt = p.title || "";
      img.width = 140;
      img.height = 140;
      img.loading = "lazy";
      imgLink.appendChild(img);
    }
    card.appendChild(imgLink);

    var nameLink = document.createElement("a");
    nameLink.className = "ma-cart__rec-mobile-name";
    nameLink.href = url;
    nameLink.textContent = p.title || "";
    card.appendChild(nameLink);

    var estrellasMov = document.createElement("div");
    estrellasMov.className = "ma-cart__rec-mobile-rating";
    card.appendChild(estrellasMov);
    attachRating(estrellasMov, p.handle);

    var price = document.createElement("div");
    price.className = "ma-cart__rec-mobile-price";
    var priceCents = precioEnCentavos(p, variant);
    price.textContent = priceCents == null ? "" : formatMoney(priceCents);
    card.appendChild(price);

    if (variant && variant.available) {
      var btn = document.createElement("button");
      btn.type = "button";
      btn.className = "ma-cart__rec-mobile-add";
      btn.setAttribute("data-ma-card-add", "");
      btn.setAttribute("data-variant-id", String(variant.id));
      btn.setAttribute("aria-label", "Agregar " + (p.title || "producto") + " al carrito");
      btn.textContent = "+ Agregar";
      card.appendChild(btn);
    } else {
      var sold = document.createElement("span");
      sold.className = "ma-cart__rec-mobile-sold";
      sold.textContent = "Agotado";
      card.appendChild(sold);
    }

    return card;
  }

  function renderList(listEl, products, isMobile) {
    while (listEl.firstChild) listEl.removeChild(listEl.firstChild);
    products.forEach(function (p) {
      listEl.appendChild(isMobile ? renderMobileCard(p) : renderCard(p));
    });
  }

  function setEmptyState(pane) {
    pane.setAttribute("data-empty", "");
    var listEl = $(LIST_SEL, pane);
    if (listEl) {
      while (listEl.firstChild) listEl.removeChild(listEl.firstChild);
    }
    var lead = $(LEAD_SEL, pane);
    var eyebrow = $(EYEBROW_SEL, pane);
    if (eyebrow) eyebrow.textContent = "Empieza tu carrito";
    if (lead) lead.textContent = "Cuando agregues productos, aquí te recomendamos qué más combina.";
  }

  function clearEmptyState(pane) {
    pane.removeAttribute("data-empty");
    var lead = $(LEAD_SEL, pane);
    var eyebrow = $(EYEBROW_SEL, pane);
    if (eyebrow) eyebrow.textContent = "Te puede gustar";
    if (lead) lead.textContent = "Productos que combinan bien con lo que ya elegiste.";
  }

  function renderToTargets(products) {
    // Desktop pane (sidebar)
    var pane = $(PANE_SEL);
    if (pane) {
      var listEl = $(LIST_SEL, pane);
      if (listEl) renderList(listEl, products.slice(0, 4), false);
    }
    // Mobile carousel (in cart body)
    var mobileWrap = $(MOBILE_SEL);
    if (mobileWrap) {
      var mobileList = $(MOBILE_LIST_SEL, mobileWrap);
      if (mobileList) {
        if (products.length === 0) {
          mobileWrap.setAttribute("hidden", "");
        } else {
          mobileWrap.removeAttribute("hidden");
          renderList(mobileList, products.slice(0, 6), true);
          // El desvanecido de los costados dice hacia dónde hay más tarjetas.
          // El carril nace oculto dentro del cajón, así que hay que avisarle
          // cuando ya tiene contenido y ancho de verdad.
          if (window.MaCarouselFade) window.MaCarouselFade.refresh();
        }
      }
    }
  }

  function clearAllTargets() {
    var pane = $(PANE_SEL);
    if (pane) {
      var listEl = $(LIST_SEL, pane);
      if (listEl) {
        while (listEl.firstChild) listEl.removeChild(listEl.firstChild);
      }
    }
    var mobileWrap = $(MOBILE_SEL);
    if (mobileWrap) {
      var mobileList = $(MOBILE_LIST_SEL, mobileWrap);
      if (mobileList) {
        while (mobileList.firstChild) mobileList.removeChild(mobileList.firstChild);
      }
      mobileWrap.setAttribute("hidden", "");
    }
  }

  function loadRecommendations() {
    if (state.inFlight) return Promise.resolve();
    var pane = $(PANE_SEL);
    var mobileWrap = $(MOBILE_SEL);
    if (!pane && !mobileWrap) return Promise.resolve();

    state.inFlight = true;

    return fetchJSON("/cart.js")
      .then(function (cart) {
        var sig = getCartSignature(cart);
        state.lastCartSig = sig;

        if (!cart.items || cart.items.length === 0) {
          if (pane) setEmptyState(pane);
          if (mobileWrap) mobileWrap.setAttribute("hidden", "");
          return;
        }
        if (pane) clearEmptyState(pane);

        // Cache hit
        var cached = readCache();
        if (cached && cached.sig === sig && Array.isArray(cached.products) && cached.products.length > 0) {
          renderToTargets(cached.products);
          return;
        }

        var cartIds = cart.items.map(function (it) {
          return it.product_id;
        });
        var uniqueIds = cartIds.filter(function (v, i, a) {
          return a.indexOf(v) === i;
        });
        // Limita para no abusar — toma los 3 últimos agregados (más relevantes)
        var seedIds = uniqueIds.slice(-3);

        // Primero los complementos: lo pequeño que acompaña a lo que ya lleva.
        var poolFetches = ACCESORIOS_HANDLES.map(fetchFallbackCollection);
        return Promise.all(poolFetches).then(function (listas) {
          // Une las dos colecciones sin repetir: comparten productos.
          var vistos = {};
          var accesorios = [];
          listas.forEach(function (lista) {
            (lista || []).forEach(function (p) {
              if (!p || !p.id || vistos[p.id]) return;
              vistos[p.id] = true;
              accesorios.push(p);
            });
          });
          var comple = ordenarComplementos(accesorios, cart, cartIds);
          if (comple.length >= 4) {
            var elegidos = comple.slice(0, 6);
            renderToTargets(elegidos);
            writeCache(sig, elegidos);
            return;
          }

          // Si no hay accesorios suficientes, se recurre a lo de siempre.
          var compFetches = seedIds.map(function (id) {
            return fetchRecsFor(id, "complementary");
          });

          return Promise.all(compFetches).then(function (compResults) {
          var compMerged = scoreAndMerge([comple].concat(compResults), cartIds);
          if (compMerged.length >= 6) {
            var top = compMerged.slice(0, 6);
            renderToTargets(top);
            writeCache(sig, top);
            return;
          }
          // Si complementary no llenó 6, complementa con related
          var relFetches = seedIds.map(function (id) {
            return fetchRecsFor(id, "related");
          });
          return Promise.all(relFetches).then(function (relResults) {
            var combined = scoreAndMerge([compMerged].concat(relResults), cartIds);
            if (combined.length >= 1) {
              var top2 = combined.slice(0, 6);
              renderToTargets(top2);
              writeCache(sig, top2);
              return;
            }
            // Último recurso: fallback collection
            var fallbackHandle = pane && pane.dataset.fallbackCollection;
            return fetchFallbackCollection(fallbackHandle).then(function (fbProducts) {
              var fallback = scoreAndMerge([fbProducts], cartIds).slice(0, 6);
              if (fallback.length > 0) {
                renderToTargets(fallback);
                writeCache(sig, fallback);
              } else {
                clearAllTargets();
              }
            });
          });
          });
        });
      })
      .catch(function (err) {
        console.warn("[ma-cart-recos] Failed to load:", err);
      })
      .then(function () {
        state.inFlight = false;
        state.loaded = true;
      });
  }

  // Invalidar caché cuando cambia el carrito
  function invalidateOnCartChange() {
    try {
      sessionStorage.removeItem(CACHE_KEY);
    } catch (e) {}
    state.loaded = false;
    state.lastCartSig = null;
  }

  // Hook: cuando se abre el drawer, carga recos. Escuchamos ambos eventos:
  //   - 'ma:cart:open' (request to open) — disparado por window.MaCart.open
  //   - 'ma:cart:opened' (already opened) — disparado por el drawer al abrir,
  //     incluyendo aperturas vía [data-ma-cart-open] buttons.
  document.addEventListener("ma:cart:open", function () {
    loadRecommendations();
  });
  document.addEventListener("ma:cart:opened", function () {
    loadRecommendations();
  });

  // Si el usuario hace ATC desde una card de recos, el handler global de
  // component-card-grid.js mete el item al carrito y abre el drawer. Hay
  // que invalidar caché para que la próxima apertura recalcule.
  document.addEventListener("click", function (e) {
    var btn = e.target.closest("[data-ma-card-add]");
    if (!btn) return;
    // Defer un tick — dejamos que el handler global haga su POST primero
    setTimeout(invalidateOnCartChange, 50);
  });

  // También invalidar cuando se quita un item del cart drawer (si hay un
  // remove handler en el futuro despachando un evento)
  document.addEventListener("ma:cart:updated", invalidateOnCartChange);

  // Carga inicial si el drawer ya está abierto al cargar la página (raro
  // pero por las dudas)
  document.addEventListener("DOMContentLoaded", function () {
    var drawer = $(DRAWER_SEL);
    if (drawer && drawer.hasAttribute("open")) {
      loadRecommendations();
    }
  });
})();
